import type { FieldDef, FormValues, TemplateDef } from '../../promotions/types';
import { familyIndustryField } from '../../promotions/data/commonFields';

export const BRIEFING_V4_PERMISSION = 'briefing-promocoes-v4';
export const COUPON_VISIBLE_KEY = 'cupomVisivelCarrinho';
export const COUPON_VISIBLE_HEADER = 'Cupom visível no carrinho?';
export const FAMILY_INDUSTRY_HEADER = 'Indústria da Família';

export const V4_REMOVED_FIELD_KEYS = new Set([
  'tipoSolicitacao', 'idIFC', 'idIfc', 'sellersExcecao',
  'skusInclusao', 'skusExclusao', 'skuCombo', 'skusObrigatorios', 'skusFamilia',
]);

export const V4_REMOVED_HEADERS = new Set([
  'Tipo de solicitação', 'ID Infracommerce', 'Sellers exceção',
  'SKUs inclusão', 'SKUs exclusão', 'SKU é combo?', 'SKUs obrigatórios', 'SKUs da família',
]);

const CUPOM_PROGRESSIVO_V4_REMOVED_FIELD_KEYS = new Set(['tipoItemExcluido', 'itensExcluidos']);
const CUPOM_PROGRESSIVO_V4_REMOVED_HEADERS = new Set([
  ...V4_REMOVED_HEADERS,
  'Tipo de item a ser EXCLUÍDO na promoção', 'Itens a serem EXCLUÍDOS da promoção',
]);

export function getRemovedV4Headers(templateId: string): ReadonlySet<string> {
  return templateId === 't6' ? CUPOM_PROGRESSIVO_V4_REMOVED_HEADERS : V4_REMOVED_HEADERS;
}

export function isBriefingV4Enabled(permissions?: ReadonlyArray<{ name?: string }> | null): boolean {
  return Boolean(permissions?.some((feature) => feature?.name === BRIEFING_V4_PERMISSION));
}

export function isRemovedV4Field(key: string, templateId?: string): boolean {
  const baseKey = key.replace(/__child\d+$/, '');
  return V4_REMOVED_FIELD_KEYS.has(baseKey) ||
    (templateId === 't6' && CUPOM_PROGRESSIVO_V4_REMOVED_FIELD_KEYS.has(baseKey));
}

export const couponVisibleField: FieldDef = {
  key: COUPON_VISIBLE_KEY,
  label: COUPON_VISIBLE_HEADER,
  type: 'select',
  required: true,
  options: [{ value: 'NÃO', label: 'NÃO' }, { value: 'SIM', label: 'SIM' }],
};

export function applyBriefingTemplateVersion(template: TemplateDef, briefingV4: boolean): TemplateDef {
  if (!briefingV4) return template;

  const sections = template.sections.map((section) => ({
    ...section,
    fields: section.fields.filter((field) => !isRemovedV4Field(field.key, template.id)).map((field) => (
      field.excludeValuesFrom && isRemovedV4Field(field.excludeValuesFrom, template.id)
        ? { ...field, excludeValuesFrom: undefined }
        : field
    )),
  })).filter((section) => section.fields.length > 0);

  if (['t5', 't6'].includes(template.id)) {
    const couponSection = sections.find((section) => section.title === 'Cupom');
    if (couponSection && !couponSection.fields.some((field) => field.key === COUPON_VISIBLE_KEY)) {
      couponSection.fields.push(couponVisibleField);
    }
  }

  if (template.id === 't6') {
    const productsSection = sections.find((section) => section.title === 'Produtos');
    if (productsSection && !productsSection.fields.some((field) => field.key === familyIndustryField.key)) {
      productsSection.fields.push({ ...familyIndustryField, label: FAMILY_INDUSTRY_HEADER });
    }
  }

  return {
    ...template,
    ...(template.id === 't6' ? { description: 'Cupom com múltiplas faixas de valor/quantidade.' } : {}),
    sections,
  };
}

export function applyBriefingValuesVersion(templateId: string, values: FormValues, briefingV4: boolean): FormValues {
  return Object.fromEntries(Object.entries(values).filter(([key]) => (
    briefingV4 ? !isRemovedV4Field(key, templateId) : key !== COUPON_VISIBLE_KEY
  )).filter(([key]) => key !== COUPON_VISIBLE_KEY || ['t5', 't6'].includes(templateId))
    .map(([key, value]) => {
      if (key !== COUPON_VISIBLE_KEY) return [key, value];
      const text = String(value).trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
      return [key, ['0', 'NAO'].includes(text) ? 'NÃO' : ['1', 'SIM'].includes(text) ? 'SIM' : value];
    }));
}

export function getCouponVisibilityError(templateId: string, values: FormValues, briefingV4 = false): string | undefined {
  if (!briefingV4 || !['t5', 't6'].includes(templateId)) return undefined;
  const value = applyBriefingValuesVersion(templateId, values, true)[COUPON_VISIBLE_KEY];
  if (value === undefined || value === null || String(value).trim() === '') {
    return `${COUPON_VISIBLE_HEADER} é obrigatório.`;
  }
  if (!['NÃO', 'SIM'].includes(String(value))) return 'Selecione NÃO ou SIM em Cupom visível no carrinho?.';
  return undefined;
}
